import { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { TIPO_SECAO_HOME_LABEL } from '../../../../model/apm/apm-enums';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { IHomeSectionItemResponse } from '../../../../model/apm/home/response/home-section-item-response';
import { ConfiguredFields } from '../../components/configured-fields';
import { FormDrawer, FormDrawerGroup, VisibilitySwitch } from '../../../../components/form-drawer';
import { toDateInputValue } from '../../../../utils/date';
import { HOME_SECTION_CONFIG, validateHomeItem } from './home-section-config';
import { HOME_SECTION_ICONS } from './home-section-icons';

interface HomeSectionItemDrawerProps {
    open: boolean;
    section?: IHomeSectionResponse;
    item?: IHomeSectionItemResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: Record<string, any>) => void;
}

export function HomeSectionItemDrawer({ open, section, item, isSaving, onClose, onSave }: HomeSectionItemDrawerProps) {
    const [form, setForm] = useState<Record<string, any>>({});
    const [showErrors, setShowErrors] = useState(false);
    const [uploading, setUploading] = useState(false);
    const isEdit = !!item;
    const config = section ? HOME_SECTION_CONFIG[section.type] : undefined;
    const fields = useMemo(() => config?.itemFields || {}, [config]);
    const noun = (config?.itemNoun || 'Item').toLowerCase();
    const errors = useMemo(() => (section ? validateHomeItem(section.type, form) : {}), [section, form]);

    useEffect(() => {
        if (!open) return;
        // A API devolve data/hora; o campo de data trabalha com AAAA-MM-DD
        setForm(item
            ? { ...item, startDate: toDateInputValue(item.startDate), endDate: toDateInputValue(item.endDate) }
            : { isVisible: true });
        setShowErrors(false);
        setUploading(false);
    }, [open, item]);

    function handleSave() {
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            return;
        }
        onSave(form);
    }

    const sectionName = section ? (section.title || TIPO_SECAO_HOME_LABEL[section.type]) : '';

    return (
        <FormDrawer
            open={open}
            icon={section ? HOME_SECTION_ICONS[section.type] : <ListAltIcon />}
            title={isEdit ? `Editar ${noun}` : `Adicionar ${noun}`}
            subtitle={`Seção: ${sectionName}`}
            saveLabel={isEdit ? 'Salvar alterações' : `Adicionar ${noun}`}
            isSaving={isSaving}
            saveDisabled={uploading}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Conteúdo" description={config?.description}>
                <Grid container spacing={2}>
                    <ConfiguredFields
                        idPrefix="home-item"
                        fields={fields}
                        value={form}
                        errors={errors}
                        showErrors={showErrors}
                        onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
                        onUploadingChange={setUploading}
                    />
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Exibição">
                <VisibilitySwitch
                    checked={form.isVisible !== false}
                    onChange={(isVisible) => setForm((current) => ({ ...current, isVisible }))}
                    description="Itens ocultos continuam salvos, mas não aparecem na home."
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
